import type { Beat, BeatMeta, Slide, V2Pack } from '../schema';

/**
 * Tiny accessors that hide the schema drift between `slides[].beatMeta` (used
 * for routing) and the parallel `pack.beats[]` (which has question/prompt for
 * pace_check & confidence_check). Keeps the state machine clean.
 */

/** Slide id is deterministic: `slide-${beatId}` per INTEGRATION.md §3.1. */
export function slideIdForBeat(beatId: string): string {
  return `slide-${beatId}`;
}

export function findSlideIndexByBeatId(pack: V2Pack, beatId: string | null | undefined): number {
  if (!beatId) return -1;
  const wanted = slideIdForBeat(beatId);
  return pack.slides.findIndex((s) => s.id === wanted);
}

export function findBeatById(pack: V2Pack, beatId: string | null | undefined): Beat | null {
  if (!beatId || !Array.isArray(pack.beats)) return null;
  return pack.beats.find((b) => b.id === beatId) ?? null;
}

export function getBeatIdForSlide(slide: Slide): string | null {
  const fromMeta = slide.beatMeta?.beatId;
  if (typeof fromMeta === 'string') return fromMeta;
  if (slide.id.startsWith('slide-')) return slide.id.slice('slide-'.length);
  return null;
}

/** Resolve the "first slide" — honor pack.entryBeatId; otherwise slide 0. */
export function findEntrySlideIndex(pack: V2Pack): number {
  if (pack.entryBeatId) {
    const idx = findSlideIndexByBeatId(pack, pack.entryBeatId);
    if (idx !== -1) return idx;
  }
  return pack.slides.length > 0 ? 0 : -1;
}

/**
 * Pick the next slide for a beat. `outcome` decides which routing key to read
 * from `beatMeta.next`. Returns -1 if no next slide is defined.
 */
export function pickNextSlideIndex(
  pack: V2Pack,
  fromSlide: Slide,
  outcome: 'default' | 'ifExpanded' | 'ifReteach' | 'ifSkipped',
): number {
  const next = (fromSlide.beatMeta ?? {}).next ?? {};
  const targetBeatId = next[outcome] ?? next.default ?? null;
  return findSlideIndexByBeatId(pack, targetBeatId);
}

export function beatKindOf(slide: Slide): BeatMeta['kind'] {
  return slide.beatMeta?.kind;
}

/** Map a clicked option index → option id ('a'/'b'/...). Prefer authored ids. */
export function optionIdForIndex(optionIds: string[] | undefined, index: number): string {
  if (Array.isArray(optionIds) && typeof optionIds[index] === 'string') {
    return optionIds[index];
  }
  return String.fromCharCode(97 + index);
}
